/**
 * Per-row validation error from the bulk-upload Lambda (BU-5). Wire shape:
 * snake_case row_level_errors[]. Field name is `row_number` to match the Lambda
 * response (verified against Level 3 re-smoke 2026-05-29).
 */
export interface RowLevelError {
  row_number: number
  field: string
  message: string
  offending_value?: string
}

/**
 * File-level rejection from the bulk-upload Lambda (BU-5). Things like missing
 * required headers, encoding issues, oversize file, etc.
 */
export interface FileLevelError {
  code?: string
  message: string
}

/**
 * Real bulk upload API response (202 Accepted).
 *
 * BU-5 fail-forward shape: when the file passes header validation, the Lambda
 * returns 202 with `accepted` set to the count of rows admitted into the Step
 * Functions execution and `row_level_errors` listing per-row issues from rows
 * that were skipped. Both arrays MAY be empty on a fully clean upload.
 *
 * Legacy shape (BU-5 pre-deploy) only had `job_id` + `status`; those fields
 * stay at the top to keep the legacy 202 path lossless.
 */
export interface BulkUploadResponse {
  job_id: string
  status: 'processing'
  accepted?: number
  file_level_errors?: FileLevelError[]
  row_level_errors?: RowLevelError[]
  /** True when >100 bad rows exist and only the first 100 are inlined */
  row_errors_truncated?: boolean
  row_errors_s3_key?: string
  error_csv_url?: string
}

/** Real bulk upload API validation error (422) */
export interface BulkUploadValidationError {
  error?: string
  error_csv_url?: string
  file_level_errors?: FileLevelError[]
  row_level_errors?: RowLevelError[]
  /** True when >100 bad rows exist and only the first 100 are inlined */
  row_errors_truncated?: boolean
  row_errors_s3_key?: string
  accepted?: number
}

/**
 * Discriminated `errorCode` for the FE service result. UI branches on this.
 *
 * - `AUTH_ERROR`: genuine session expiry (no active session, or
 *   authenticateSession returns falsy). Resolves to the session-expired copy.
 * - `UPLOAD_REJECTED`: backend Lambda rejected the upload despite a valid
 *   session. Distinct from AUTH_ERROR. Resolves to result.error, NOT the
 *   session-expired copy. See EOM-499.
 * - `FILE_LEVEL`: header/encoding rejection. Stay on upload screen, show banner.
 * - `ROW_LEVEL_ALL`: all rows failed. Open UploadErrorsModal even though SFN
 *   never started.
 * - `LEGACY_VALIDATION`: BU-5 not yet deployed in this env. Fall back to existing
 *   toast/upload-stage retry flow. Removed once BU-5 ships everywhere.
 */
export const BULK_UPLOAD_ERROR_CODES = [
  'AUTH_ERROR',
  'UPLOAD_REJECTED',
  'NO_GROUP',
  'AUTHORIZATION_ERROR',
  'MISSING_FILE',
  'API_ERROR',
  'UNKNOWN',
  'FILE_LEVEL',
  'ROW_LEVEL_ALL',
  'LEGACY_VALIDATION',
  'TRANSIENT_INFRA',
] as const

export type BulkUploadErrorCode = (typeof BULK_UPLOAD_ERROR_CODES)[number]

/** Server action result (discriminated union) */
export type BulkUploadResult =
  | { success: true; data: BulkUploadResponse }
  | {
      success: false
      error: string
      errorCode: BulkUploadErrorCode
      errorCsvUrl?: string
      fileLevelErrors?: FileLevelError[]
      rowLevelErrors?: RowLevelError[]
      retryAfterSeconds?: number
      /**
       * Mirrors BulkUploadResponse.row_errors_truncated for the ROW_LEVEL_ALL
       * path
       */
      rowErrorsTruncated?: boolean
      /** Mirrors BulkUploadResponse.row_errors_s3_key for the ROW_LEVEL_ALL path */
      rowErrorsS3Key?: string
    }

/**
 * CSV template headers matching the backend enrollment API schema. Source:
 * roster_dummy.csv from Jeff Fate's bulk upload pipeline.
 */
export const CSV_TEMPLATE_HEADERS = [
  'EID',
  'First Name',
  'Middle Name',
  'Last Name',
  'Relationship',
  'Subgroup',
  'SSN',
  'Birth Date',
  'Sex',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'Zip Code',
  'Personal Phone',
  'Work Phone',
  'Mobile Phone',
  'Email',
  'Hire Date',
  'Status',
  'Sidecar Health Plan',
] as const

/** Employee-level error/review detail from job status */
export interface EmployeeIssue {
  employeeName: string
  detail: string
}

/** Job status API response */
export interface JobStatusResponse {
  status: 'processing' | 'completed' | 'failed'
  jobId: string
  employerGroupUuid: string
  createdAt: string
  totalRows: number
  completedAt?: string
  failedAt?: string
  errorMessage?: string
  summary?: {
    total: number
    succeeded: number
    underReview: number
    failed: number
    skipped: number
  }
  errors?: EmployeeIssue[]
  underReview?: EmployeeIssue[]
  resultCsvUrl?: string
  errorCsvUrl?: string
}

/**
 * Detail captured from the bulk-upload 202 at submit time and carried forward
 * through the async job lifecycle. The async job-status poll cannot recover
 * row-level structure (the DDB job record collapses to {name, detail}), so we
 * snapshot it here. Feeds the NeedsFixesModal.
 */
export interface BulkUploadSubmitResult {
  accepted: number
  rowLevelErrors: RowLevelError[]
  errorCsvUrl?: string
  rowErrorsS3Key?: string
  rowErrorsTruncated?: boolean
}

/** Persisted upload job state in localStorage */
export interface StoredUploadJob {
  jobId: string
  submittedAt: string
  status: 'processing' | 'completed' | 'failed'
  result?: JobStatusResponse
  submitResult?: BulkUploadSubmitResult
}

/**
 * Namespace prefix for the upload-job localStorage entry. Sweep target on
 * logout. The user- and group-scoped key is produced by
 * uploadJobStorageKey(userId, groupUuid).
 */
export const UPLOAD_JOB_STORAGE_KEY_PREFIX = 'eom140_uploadJob'

/**
 * Returns the localStorage key for the current user's upload job, scoped to a
 * specific company. Scoping by userId prevents one user's "Last upload
 * completed" card from leaking to a different user logged in on the same
 * browser. Scoping by groupUuid additionally prevents a multi-group user's card
 * from leaking across a group switch: uploading a census for Group A then
 * switching to Group B must not surface Group A's card (EOM-380).
 */
export function uploadJobStorageKey(userId: string, groupUuid: string): string {
  return `${UPLOAD_JOB_STORAGE_KEY_PREFIX}:${userId}:${groupUuid}`
}
